/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1687305600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Jun 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "notion",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Connect to Notion",
          "depth": 0,
          "id": "connect-to-notion"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Database selection",
          "depth": 1,
          "id": "database-selection"
        }, {
          "value": "Record matching",
          "depth": 1,
          "id": "record-matching"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      p: "p",
      h2: "h2",
      strong: "strong",
      h3: "h3",
      img: "img",
      h4: "h4",
      ul: "ul",
      li: "li",
      code: "code"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Database rows"
            }), _jsxs(_components.td, {
              children: ["Upsert or update rows in a Notion ", _jsx(_components.a, {
                href: "https://www.notion.so/help/intro-to-databases",
                children: "database"
              })]
            }), _jsx(_components.td, {
              children: "Upsert, Update"
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-notion",
        children: "Connect to Notion"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Notion"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to Notion by logging into Notion."]
      }), "\n", _jsx(_components.p, {
        children: "Next, select which Notion pages you want to give Hightouch access to."
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "Be sure to give access to pages that include databases you want to sync to.\nIf the page containing the database within Notion has restricted access, you won't be able to configure a sync to it."
        })
      }), "\n", _jsx(_components.p, {
        children: "Once successful, you will be redirected back to Hightouch to enter a descriptive name for your destination and complete setup."
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Notion destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Notion destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "database-selection",
        children: "Database selection"
      }), "\n", _jsxs(_components.p, {
        children: ["After selecting your desired ", _jsx(_components.a, {
          href: "#supported-syncing",
          children: "sync mode"
        }), ", select the Notion database you want to sync data to."]
      }), "\n", _jsx(_components.h3, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["To match rows from your model to database rows in Notion, you need to select a model column and corresponding Notion field.\nIt's best to select a unique identifier for record matching, otherwise the intended records may not be updated properly.\nRefer to the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching docs"
        }), " for more information."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-notion-record-matching.png",
          alt: "Record matching in the Hightouch UI"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["The Notion field you select for record matching ", _jsxs(_components.a, {
            href: "#column-not-appearing-for-record-matching",
            children: ["must be a ", _jsx(_components.strong, {
              children: "text"
            }), " field"]
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "You can sync model columns to Notion's database properties.\nHightouch automatically detects existing database properties from your Notion databases."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-field-mapping.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "supported-notion-properties",
        children: "Supported Notion properties"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports the following Notion database properties when field mapping."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Checkbox"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Date"
          }), ": Hightouch supports a single date, and not a date range"]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Multi Select"
          })
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "Number"
          }), ": If a number field is passed an invalid number, Hightouch attempts to convert it to a number. If the conversion result is invalid, the sync errors."]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Rich Text"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Select"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "Title"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "URL"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To learn more about Notion's available properties, please refer to ", _jsx(_components.a, {
          href: "https://developers.notion.com/reference/property-object",
          children: "Notion's docs"
        }), "."]
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/syncs/overview#delete-behavior",
          children: "delete behavior"
        }), " you select dictates what to do when a row no longer appears in your model's query results. You have the following options:"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.strong, {
            children: "Delete"
          }), " is only available on ", _jsx(_components.strong, {
            children: "Upsert"
          }), " mode and ", _jsx(_components.strong, {
            children: "Clear"
          }), " is available for\n", _jsx(_components.strong, {
            children: "Upsert"
          }), " and ", _jsx(_components.strong, {
            children: "Update"
          }), " mode."]
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Behavior"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Do nothing"
              })
            }), _jsx(_components.td, {
              children: "Keep the row in the database with all its synced fields"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Clear"
              })
            }), _jsx(_components.td, {
              children: "Clear all the mapped fields, but keep the row in the database"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Delete"
              })
            }), _jsx(_components.td, {
              children: "Delete the synced row in the database"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "database-not-appearing-in-dropdown",
        children: "Database not appearing in dropdown"
      }), "\n", _jsxs(_components.p, {
        children: ["If the page containing the database within Notion has restricted access, the database won't appear in the dropdown menu of the sync configuration, even if Hightouch is explicitly granted access during authentication.\nYou may also receive a ", _jsx(_components.code, {
          children: "404 - Could not find database with ID: {...}"
        }), " error in the ", _jsx(_components.a, {
          href: "/syncs/record-matching",
          children: "record matching"
        }), " section of your sync configuration, or when attempting to sync to Notion."]
      }), "\n", _jsxs(_components.p, {
        children: ["To fix this, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and select your Notion destination.\nClick ", _jsx(_components.strong, {
          children: "Log in to Notion"
        }), " and add access to any required pages."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-notion-log-in.png",
          alt: "Notion OAuth in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Then, return to your sync configuration or create a new one from the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), ".\nYou may need to click the refresh button to see the new database."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-notion-refresh.png",
          alt: "Refreshing Notion databses in the Hightouch UI"
        })
      }), "\n", _jsx(_components.h4, {
        id: "column-not-appearing-for-record-matching",
        children: "Column not appearing for record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch only supports record matching on ", _jsx(_components.strong, {
          children: "text"
        }), " fields in Notion.\nConvert the field type to text in Notion, or create a new text field."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-notion-record-matching-text-type.png",
          alt: "Text fields in Notion"
        })
      }), "\n", _jsx(_components.h4, {
        id: "400---body-failed-validation--should-be-defined-instead-was-undefined",
        children: "400 - body failed validation {...} should be defined, instead was undefined"
      }), "\n", _jsxs(_components.p, {
        children: ["This error occurs if you try to sync ", _jsx(_components.code, {
          children: "null"
        }), " values to ", _jsx(_components.a, {
          href: "https://developers.notion.com/reference/property-object#select",
          children: "Select"
        }), " or ", _jsx(_components.a, {
          href: "https://developers.notion.com/reference/property-object#multi-select",
          children: "Multi-select"
        }), " fields in Notion.\nTo resolve this, check the ", _jsxs(_components.a, {
          href: "/syncs/mapping-data#dont-sync-null-values",
          children: [_jsx(_components.strong, {
            children: "Don't sync null values"
          }), " option"]
        }), " in the template mapper or ", _jsxs(_components.a, {
          href: "/models/sql-editor#exclude-rows-with-null-values",
          children: ["remove rows containing ", _jsx(_components.code, {
            children: "null"
          }), " values"]
        }), " from your model's query results.\nYou need to do this option for all field mappings to ", _jsx(_components.a, {
          href: "https://developers.notion.com/reference/property-object#select",
          children: "Select"
        }), " or ", _jsx(_components.a, {
          href: "https://developers.notion.com/reference/property-object#multi-select",
          children: "Multi-select"
        }), " fields in Notion, if you believe the mapped model columns might contain ", _jsx(_components.code, {
          children: "null"
        }), " values."]
      }), "\n", _jsx(_components.h4, {
        id: "400---body-failed-validation--should-be-a-string-instead-was-",
        children: "400 - body failed validation {...} should be a string, instead was {...}"
      }), "\n", _jsxs(_components.p, {
        children: ["This error occurs if you try to sync non-text values to a ", _jsx(_components.a, {
          href: "https://developers.notion.com/reference/property-object#rich-text",
          children: "Text"
        }), " field in Notion.\nTo resolve this, ", _jsx(_components.a, {
          href: "/models/data-types-casting#casting-from-your-model-configuration",
          children: "cast"
        }), " the relevant column or columns from your model to a ", _jsx(_components.strong, {
          children: "String"
        }), " type."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
